import React, { useEffect, useState } from "react";
import { client } from "../../client";
import { AiOutlineCheck, AiOutlineClose, AiOutlineDelete } from "react-icons/ai";

const AdminApproval = () => {
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reasonInput, setReasonInput] = useState("");
  const [activeActionPin, setActiveActionPin] = useState(null);
  const [actionType, setActionType] = useState(null); 

  const fetchPins = async () => {
    const query = `
      *[_type == "pin" && (status == "pending" || status == "approved")] | order(_createdAt desc) {
        _id,
        title,
        status,
        image {
          asset -> {
            url
          }
        },
        postedBy -> {
          _id,
          userName,
          image
        },
        adminReason
      }
    `;
    const data = await client.fetch(query);
    setPins(data);
    setLoading(false);
  };

  // Approve pin
  const approvePin = async (id) => {
    await client
      .patch(id)
      .set({ status: "approved", adminReason: "" })
      .commit()
      .then(() => {
        setPins((prev) => prev.filter((pin) => pin._id !== id));
      });
  };

  // Reject pin with reason
  const rejectPin = async (id) => {
    if (!reasonInput.trim()) {
      alert("Please enter a reason before rejecting.");
      return;
    }
    await client
      .patch(id)
      .set({ status: "rejected", adminReason: reasonInput })
      .commit()
      .then(() => {
        setPins((prev) => prev.filter((pin) => pin._id !== id));
        setReasonInput("");
        setActiveActionPin(null);
        setActionType(null);
      });
  };

  // Remove approved pin with reason
  const removePin = async (id) => {
    if (!reasonInput.trim()) {
      alert("Please enter a reason before removing.");
      return;
    }
    await client
      .patch(id)
      .set({ status: "removed", adminReason: reasonInput })
      .commit()
      .then(() => {
        setPins((prev) => prev.filter((pin) => pin._id !== id));
        setReasonInput("");
        setActiveActionPin(null);
        setActionType(null);
      });
  };

  useEffect(() => {
    fetchPins();
  }, []);

  if (loading) {
    return <p className="text-center mt-10">Loading pins...</p>;
  }

  if (pins.length === 0) {
    return <p className="text-center mt-10">No pins for review 🎉</p>;
  }

  return (
    <div className="p-5">
      <h1 className="text-2xl font-bold mb-5">Admin - Manage Pins</h1>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {pins.map((pin) => (
          <div
            key={pin._id}
            className="bg-white rounded-lg shadow p-4 flex flex-col"
          >
            <img
              src={pin.image?.asset?.url}
              alt={pin.title}
              className="rounded-lg mb-3 h-48 w-full object-cover"
            />
            <h2 className="font-semibold text-lg">{pin.title}</h2>
            <p className="text-sm mt-1">
              Status:{" "}
              <span
                className={
                  pin.status === "approved"
                    ? "text-green-600"
                    : pin.status === "pending"
                      ? "text-yellow-600"
                      : "text-red-600"
                }
              >
                {pin.status}
              </span>
            </p>
            <div className="flex items-center mt-2">
              <img
                src={pin.postedBy?.image}
                alt={pin.postedBy?.userName}
                className="w-8 h-8 rounded-full mr-2"
              />
              <span className="text-sm">{pin.postedBy?.userName}</span>
            </div>

            {/* Action buttons */}
            {activeActionPin === pin._id ? (
              <div className="w-full mt-3">
                <textarea
                  value={reasonInput}
                  onChange={(e) => setReasonInput(e.target.value)}
                  placeholder={`Enter ${actionType} reason...`}
                  className="w-full border rounded p-2 mb-2"
                />
                <div className="flex space-x-2">
                  {actionType === "reject" ? (
                    <button
                      onClick={() => rejectPin(pin._id)}
                      className="flex-1 bg-red-500 text-white p-2 rounded hover:bg-red-600"
                    >
                      Confirm Reject
                    </button>
                  ) : (
                    <button
                      onClick={() => removePin(pin._id)}
                      className="flex-1 bg-red-500 text-white p-2 rounded hover:bg-red-600"
                    >
                      Confirm Remove
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setActiveActionPin(null);
                      setReasonInput("");
                      setActionType(null);
                    }}
                    className="flex-1 bg-gray-300 p-2 rounded hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex mt-4 space-x-2">
                {pin.status === "pending" && (
                  <>
                    <button
                      onClick={() => approvePin(pin._id)}
                      className="flex-1 bg-green-500 text-white p-2 rounded hover:bg-green-600 flex items-center justify-center"
                    >
                      <AiOutlineCheck className="mr-1" /> Approve
                    </button>
                    <button
                      onClick={() => {
                        setActiveActionPin(pin._id);
                        setActionType("reject");
                      }}
                      className="flex-1 bg-red-500 text-white p-2 rounded hover:bg-red-600 flex items-center justify-center"
                    >
                      <AiOutlineClose className="mr-1" /> Reject
                    </button>
                  </>
                )}
                {pin.status === "approved" && (
                  <button
                    onClick={() => {
                      setActiveActionPin(pin._id);
                      setActionType("remove");
                    }}
                    className="flex-1 bg-red-500 text-white p-2 rounded hover:bg-red-600 flex items-center justify-center"
                  >
                    <AiOutlineDelete className="mr-1" /> Remove
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminApproval;